import { NavLink, Outlet } from 'react-router-dom';
import { AppNav, type AppNavLinkSpec, Button } from '@rmint/ui';
import { useAuth } from '../../lib/auth';
import { env } from '../../lib/env';
import { ToastProvider } from '../../lib/toast';
import { Toaster } from '../ui/Toaster';

/**
 * Shared app shell — dark AppNav + outlet + toast. Role-agnostic: each
 * role-app supplies its own `links` (nav points only at that app's routes).
 * Lifted from apps/web/routes/Layout.tsx during the Phase C split.
 */
export function AppLayout({ links }: { links: AppNavLinkSpec[] }) {
  const { ready, authenticated, email, privyDid, login, logout } = useAuth();

  return (
    <ToastProvider>
      <div className="min-h-full flex flex-col">
        <AppNav
          brandHref="/"
          links={links}
          right={
            <UserChip
              ready={ready}
              authenticated={authenticated}
              email={email}
              privyDid={privyDid}
              onLogin={login}
              onLogout={logout}
            />
          }
          banner={env.devAuthBypass ? <DevBypassBanner /> : null}
        />
        <main className="flex-1 mx-auto max-w-7xl w-full px-6 py-6">
          <Outlet />
        </main>
        <Toaster />
      </div>
    </ToastProvider>
  );
}

function UserChip({
  ready,
  authenticated,
  email,
  privyDid,
  onLogin,
  onLogout,
}: {
  ready: boolean;
  authenticated: boolean;
  email: string | undefined;
  privyDid: string | undefined;
  onLogin: () => void;
  onLogout: () => Promise<void>;
}) {
  if (!ready) {
    return <span className="text-xs text-white/55">…</span>;
  }
  // In dev-bypass mode the synthetic identity already drives all
  // requests; surfacing a "Sign in" button is misleading because
  // there's nothing to sign in to (no Privy app id is configured).
  // The header banner already announces the bypass — no need to
  // duplicate.
  if (env.devAuthBypass && !authenticated) {
    return null;
  }
  if (!authenticated) {
    return (
      <Button variant="primary" size="sm" onClick={onLogin}>
        Sign in
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <NavLink
        to="/profile"
        className="text-right hover:opacity-80 transition-opacity"
        title="View profile"
      >
        <div className="text-sm text-white">{email ?? 'profile'}</div>
        <div className="text-[11px] text-white/65 font-mono">
          {privyDid ? truncateDid(privyDid) : ''}
        </div>
      </NavLink>
      <Button
        variant="ghost"
        size="sm"
        onClick={onLogout}
        className="!text-white !border-white/20 hover:!bg-white/10"
      >
        Sign out
      </Button>
    </div>
  );
}

function truncateDid(did: string): string {
  if (did.length < 24) return did;
  return `${did.slice(0, 14)}…${did.slice(-6)}`;
}

function DevBypassBanner() {
  return (
    <div className="bg-warn-bg text-warn text-xs px-6 py-1 text-center">
      DEV_AUTH_BYPASS=true — calls run as <code>did:privy:dev-bypass</code>.
    </div>
  );
}
